import React, { useContext, useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import EmailLoggerTable from "./EmailLoggerTable";
import EmailLoggerCards from "./EmailLoggerCards";
import SearchForm from "./SearchForm";
import AdminemailLogsContext from "../../context/adminemailLogs/adminemailLogsContext";
import FilterSidebar from "./FilterSidebar";
import ColumnManager from "./ColumnManager";
import ExportExcelButton from "./ExportButton";
import { FaSpinner } from "react-icons/fa";

const EmailFormList = ({ history }) => {
  const adminemailLogsContext = useContext(AdminemailLogsContext);
  const {
    entries,
    currentPage,
    pageSize,
    totalCount,
    loading,
    searchText,
    setCurrentPage,
    appliedFilters,
    getEntries,
    searchFilter,
    clearSearchFilter,
    filteredEntries,
    listView,
    setListView,
  } = adminemailLogsContext;

  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false);

  const itemContent = entries || filteredEntries || [];

  const totalPages = Math.ceil(totalCount / pageSize);
  const startRecord = ((currentPage - 1) * pageSize) + 1;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  // Get current filters for pagination
  const getCurrentFilters = () => {
    const baseFilters = {
      pageSize,
    };

    // Add filters from appliedFilters if they exist
    if (appliedFilters) {
      if (appliedFilters.status) baseFilters.status = appliedFilters.status;
      if (appliedFilters.learningCenterName) baseFilters.learningCenterName = appliedFilters.learningCenterName;
      if (appliedFilters.learningCenterCharter) baseFilters.learningCenterCharter = appliedFilters.learningCenterCharter;
      if (appliedFilters.age) baseFilters.age = appliedFilters.age;
      if (appliedFilters.startDate) baseFilters.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) baseFilters.endDate = appliedFilters.endDate;
    }

    // Add search text if exists
    if (searchText && searchText.trim() !== '') {
      baseFilters.searchText = searchText;
    }

    return baseFilters;
  };

  const handleNextPage = async () => {
    if (currentPage < totalPages && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      try {
        const filters = getCurrentFilters();
        await getEntries({
          ...filters,
          page: nextPage,
        });
      } catch (error) {
        console.error('Error fetching next page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (currentPage > 1 && !loading) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);

      try {
        const filters = getCurrentFilters();
        await getEntries({
          ...filters,
          page: prevPage,
        });
      } catch (error) {
        console.error('Error fetching previous page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  // Go to specific page
  const handlePageClick = async (pageNumber) => {
    if (pageNumber !== currentPage && !loading && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);

      try {
        const filters = getCurrentFilters();
        await getEntries({
          ...filters,
          page: pageNumber,
        });
      } catch (error) {
        console.error('Error fetching page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  // Reset to page 1 when search results change and current page is invalid
  useEffect(() => {
    const isSearchResult = searchText && searchText.trim() !== '';
    const isInvalidSearchPage = isSearchResult && itemContent.length === 0 && currentPage > 1;

    if (isInvalidSearchPage && totalCount > 0) {
      setCurrentPage(1);
    }
  }, [itemContent, currentPage, searchText, totalCount, setCurrentPage]);

  // Show pagination if we have more records than pageSize and not loading
  const showPagination = totalCount > pageSize && !loading && itemContent.length > 0;

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Load initial data
  useEffect(() => {
    if (!entries || entries.length === 0) {
      //console.log("Loading initial email log data");
      const initialFilters = {
        page: 1,
        pageSize: pageSize
      };
      getEntries(initialFilters);
    }
  }, []);

  return (
    <div className="forms-list">
      {/* Header Section */}
      <div className="list-header">
        <div className="list-header-left">
          <h2 className="list-title">Email Logs</h2>
          <p className="list-subtitle">Manage and review Email Log submissions</p>
        </div>
      </div>

      {/* Toolbar Section */}
      <div className="list-toolbar-container">
        <div className="list-toolbar">
          <SearchForm />

          <button
            className="toolbar-btn filter-btn"
            onClick={() => setIsFilterSidebarOpen(true)}
          >
            <FaIcons.FaFilter />
            Filters
          </button>

          <button
            className="toolbar-btn columns-btn"
            onClick={() => setIsColumnManagerOpen(true)}
          >
            <FaIcons.FaColumns />
            Columns
          </button>
          <div className="toolbar-btn-group">

          <button
            className={`toolbar-btn view-btn ${listView ? 'active' : ''}`}
            onClick={() => setListView(true)}
            title="List View"
          >
            <FaIcons.FaList />
          </button>

          <button
            className={`toolbar-btn view-btn ${!listView ? 'active' : ''}`}
            onClick={() => setListView(false)}
            title="Grid View"
          >
            <FaIcons.FaThLarge />
          </button>
          </div>

          <ExportExcelButton getCurrentFilters={getCurrentFilters} />
        </div>
      </div>

      {/* Record Count */}
      {loading ? (
        <div className="record-count">
          <p>Loading...</p>
        </div>
      ) : (
        <p className="record-count">
          {totalCount > 0
            ? `${startRecord}-${endRecord} of ${totalCount} Records`
            : '0 Records'
          }
        </p>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="interest-complete">
          <FaSpinner className="spinner-icon" />
          <p>Loading Please wait...</p>
        </div>
      ) : (
        <>
          {/* Data Table or Cards or Empty State */}
          {itemContent.length > 0 ? (
            listView ? (
              <EmailLoggerTable items={itemContent} history={history} />
            ) : (
              <EmailLoggerCards items={itemContent} />
            )
          ) : (
            <div className="interest-complete">
              <FaIcons.FaCheckSquare className="done-icon" />
              <p>No records found matching your search criteria</p>
            </div>
          )}

          {/* Pagination Controls */}
          {showPagination && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
              >
                <FaIcons.FaChevronLeft />
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="pagination-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
              >
                <FaIcons.FaChevronRight />
              </button>
            </div>
          )}
        </>
      )}

      {/* Filter Sidebar */}
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
      />

      {/* Column Manager */}
      <ColumnManager
        isOpen={isColumnManagerOpen}
        onClose={() => setIsColumnManagerOpen(false)}
      />
    </div>
  );
};

export default EmailFormList;
